import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, Calendar, ShieldCheck } from 'lucide-react';
import { useLang } from '../../../context/LangContext';
import { fadeInUp } from '../../../animations/variants';

const CALENDLY_URL = "https://calendly.com/elite-help";
const faqKeys = [1, 2, 3, 4];

export const TarifsCTA = () => {
  const { t } = useLang();
  const [openIndex, setOpenIndex] = useState(null);
  const guarantees = ["tarifs.guarantee.1", "tarifs.guarantee.2", "tarifs.guarantee.3", "tarifs.guarantee.4"];

  return (
    <>
      {/* FAQ */}
      <section data-testid="tarifs-faq" className="relative py-24 bg-[#0A0A0A]">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <motion.div {...fadeInUp} className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-white tracking-tight">{t("tarifs.faq.title")}</h2>
          </motion.div>
          <div className="space-y-0">
            {faqKeys.map((num, i) => {
              const isOpen = openIndex === i;
              return (
                <motion.div key={num}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.05 }}
                  className="border-b border-neutral-800"
                >
                  <button data-testid={`tarifs-faq-${i}`}
                    className="w-full flex items-center justify-between py-5 text-left group"
                    onClick={() => setOpenIndex(isOpen ? null : i)}>
                    <span className="text-sm md:text-base font-medium text-white group-hover:text-brand-green transition-colors pr-4">
                      {t(`tarifs.faq.${num}.q`)}
                    </span>
                    <motion.div animate={{ rotate: isOpen ? 180 : 0 }} transition={{ duration: 0.2 }}>
                      <ChevronDown size={18} className="text-neutral-500 flex-shrink-0" />
                    </motion.div>
                  </button>
                  <AnimatePresence>
                    {isOpen && (
                      <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3 }} className="overflow-hidden">
                        <p className="text-sm text-neutral-400 leading-relaxed pb-5">{t(`tarifs.faq.${num}.a`)}</p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA - Only "Réserver un appel" button, no "Voir les tarifs" */}
      <section data-testid="tarifs-cta" className="relative py-24 bg-[#0A0A0A] overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(84,161,51,0.08),transparent_70%)]" />
        <div className="relative z-10 max-w-3xl mx-auto px-6 text-center">
          <motion.h2 {...fadeInUp} className="text-3xl md:text-5xl font-bold text-white tracking-tight mb-4">
            {t("tarifs.cta.title")}
          </motion.h2>
          <motion.p {...fadeInUp} className="text-neutral-400 text-lg mb-10">
            {t("tarifs.cta.subtitle")}
          </motion.p>
          <motion.div 
            className="flex items-center justify-center"
            initial={{ opacity: 0, y: 20 }} 
            whileInView={{ opacity: 1, y: 0 }} 
            viewport={{ once: true }} 
            transition={{ delay: 0.2 }}
          >
            {/* Only Réserver un appel -> Calendly */}
            <a
              href={CALENDLY_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="tarifs-cta-calendly"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl bg-gradient-to-r from-brand-green to-brand-blue text-white font-semibold transition-all hover:shadow-lg hover:shadow-brand-green/20"
            >
              <Calendar size={18} />
              {t("cta.secondary")}
            </a>
          </motion.div>
        </div>
      </section>

      {/* Guarantee Bar */}
      <section data-testid="guarantee-bar" className="border-t border-white/[0.05] bg-[#0A0A0A] py-8">
        <div className="max-w-6xl mx-auto px-6 flex flex-wrap justify-center gap-6 md:gap-10">
          {guarantees.map((key, i) => (
            <motion.div key={i} className="flex items-center gap-2 text-sm text-neutral-500"
              initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}>
              <ShieldCheck size={16} className="text-brand-green/50" />
              {t(key)}
            </motion.div>
          ))}
        </div>
      </section>
    </>
  );
};
